import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import cx from "classnames"

const sizes = {
  xs: 24,
  sm: 50,
  md: 90
}

/**
 * Previously known as Gravatar component
 * Avatar gets an account user's profile image
 * When there's no uploaded image, it falls back
 * to gravatar
 * @param {string} email - Renders an avatar by users email. Defaults to upload profile picture, then tries gravatar
 * @param {("xs"|"sm"|"md")} size - The size of the avatar
 * @param {("circle"|"square")} shape - The shape of the avatar
 * @param {string} source - This helps the server know to only attempt loading by email from specified source e.g gravatar
 * @param {string} src - A string URL for avatar that overrides attempt to get image by email address
 * @param {object} file - A blob object for avatar that overrides attempt to get image by email address
 * @param {string} className
 * @param {object} style
 * @example ```
 * <Avatar email="nathan@convertkit.com" />
 * <Avatar email="janet@hotmail.com" source="gravatar" /> # Janet has no convertkit account so no need hitting the DB to check
 * <Avatar src="santa.jpg" /> # Need the Avatar component but want to specify your own image source
 * <Avatar email="santa@northpole.org" src="santa.jpg" /> # src will override any attempt to get image by email
 * <Avatar email="msclause@northpole.org" file={fileObject} /> # file will use a data-uri from file and override email
 * ```
 */
export function Avatar({
  email,
  size = "xs",
  shape = "circle",
  source = "default",
  src = "",
  alt,
  width,
  height,
  file,
  className,
  style,
  ...props
}) {
  const [fileSrc, setFileSrc] = useState()
  const pixelSize = sizes[size]
  const classes = cx("object-cover object-center bg-gray-100", className, {
    "rounded-full": shape === "circle",
    rounded: shape === "square"
  })
  const styles = { width: pixelSize, height: pixelSize, ...style }

  const emailSrc = `/user/avatar?email=${encodeURIComponent(
    email
  )}&source=${source}`

  useEffect(() => {
    if (!file) return
    const reader = new FileReader()
    const readerLoad = event => {
      setFileSrc(event.target.result)
    }
    reader.addEventListener("load", readerLoad)
    reader.readAsDataURL(file)
    return () => {
      reader.removeEventListener("load", readerLoad)
    }
  }, [file])

  return (
    <img
      className={classes}
      style={styles}
      width={width || styles.width}
      height={height || styles.height}
      alt={alt || email ? `Avatar for ${email}` : ""}
      src={src || fileSrc || emailSrc}
      {...props}
    />
  )
}

Avatar.propTypes = {
  email: PropTypes.string.isRequired,
  size: PropTypes.oneOf(Object.keys(sizes)),
  shape: PropTypes.oneOf(["square", "circle"]),
  source: PropTypes.oneOf(["default", "gravatar"]),
  className: PropTypes.string,
  style: PropTypes.object,
  src: PropTypes.string,
  alt: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  file: PropTypes.object
}
